import { useState, useCallback } from 'react';
import { caySamApiService, AddNhatKyBatchData } from 'src/api/caysam-api';
import { CaySam, CaySamNhatKy } from 'src/types/caysam';
import { LoSamCamera } from 'src/types/camera';

// ----------------------------------------------------------------------

type UseGetCaySamReturn = {
  caySam: CaySam | null;
  caySamNhatKy: CaySamNhatKy[];
  cameras: LoSamCamera[]; // <--- [MỚI] Danh sách camera
  
  // State loading
  loading: boolean;
  loadingNhatKy: boolean;
  loadingAddNhatKy: boolean;
  loadingAddFile: boolean;
  loadingCameras: boolean; // <--- [MỚI] Loading cho camera

  // State error
  error: string | null;
  errorNhatKy: string | null;
  errorAddNhatKy: string | null;
  errorAddFile: string | null;
  errorCameras: string | null; // <--- [MỚI] Error cho camera

  // Actions
  getCaySam: (id: string) => Promise<void>;
  getCaySamNhatKy: (id: string) => Promise<void>;
  getCamerasByLoSamId: (id: string) => Promise<void>; // <--- [MỚI] Hàm lấy danh sách camera
  addNhatKyByCaySamIds: (data: AddNhatKyBatchData, files?: File[]) => Promise<boolean>;
  addDinhKemFile: (caySamIds: string[], file: File) => Promise<boolean>;
  
  clearData: () => void;
};

export default function useGetCaySam(): UseGetCaySamReturn {
  const [caySam, setCaySam] = useState<CaySam | null>(null);
  const [caySamNhatKy, setCaySamNhatKy] = useState<CaySamNhatKy[]>([]);
  const [cameras, setCameras] = useState<LoSamCamera[]>([]); // <--- [MỚI]

  const [loading, setLoading] = useState(false);
  const [loadingNhatKy, setLoadingNhatKy] = useState(false);
  const [loadingAddNhatKy, setLoadingAddNhatKy] = useState(false);
  const [loadingAddFile, setLoadingAddFile] = useState(false);
  const [loadingCameras, setLoadingCameras] = useState(false); // <--- [MỚI]

  const [error, setError] = useState<string | null>(null);
  const [errorNhatKy, setErrorNhatKy] = useState<string | null>(null);
  const [errorAddNhatKy, setErrorAddNhatKy] = useState<string | null>(null);
  const [errorAddFile, setErrorAddFile] = useState<string | null>(null);
  const [errorCameras, setErrorCameras] = useState<string | null>(null); // <--- [MỚI]

  // Lấy thông tin chi tiết cây sâm
  const getCaySam = useCallback(async (id: string) => {
    if (!id) {
      setError('ID cây sâm không hợp lệ');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await caySamApiService.getCaySam(id);
      
      if (response.messCode === 1 && response.oneItem) {
        setCaySam(response.oneItem as CaySam);
      } else {
        setError(response.message || 'Không thể tải dữ liệu cây sâm');
        setCaySam(null);
      }
    } catch (err: any) {
      console.error('Error fetching cay sam:', err);
      setError(err.message || 'Có lỗi xảy ra khi tải dữ liệu cây sâm');
      setCaySam(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Lấy nhật ký cây sâm
  const getCaySamNhatKy = useCallback(async (id: string) => {
    if (!id) {
      setErrorNhatKy('ID cây sâm không hợp lệ');
      return;
    }

    try {
      setLoadingNhatKy(true);
      setErrorNhatKy(null);
      
      const response = await caySamApiService.getCaySamNhatKy(id);
      if (response.messCode === 1) {
        const arr = Array.isArray(response.items) ? (response.items as CaySamNhatKy[]) : [];
        setCaySamNhatKy(arr);
        
      } else {
        setErrorNhatKy(response.message || 'Không thể tải dữ liệu nhật ký cây sâm');
        setCaySamNhatKy([]);
      }
    } catch (err: any) {
      console.error('Error fetching cay sam nhat ky:', err); 
      setErrorNhatKy(err.message || 'Có lỗi xảy ra khi tải dữ liệu nhật ký cây sâm');
      setCaySamNhatKy([]);
    } finally {
      setLoadingNhatKy(false);
    }
  }, []);

  // [MỚI] Lấy danh sách Camera theo Lô Sâm ID
  const getCamerasByLoSamId = useCallback(async (id: string) => {
    if (!id) return;

    try {
      setLoadingCameras(true);
      setErrorCameras(null);

      const response = await caySamApiService.OptionCameraByLoSamAndUser(id);

      if (response.messCode === 1) {
        const listCameras = Array.isArray(response.items) ? (response.items as LoSamCamera[]) : [];
        setCameras(listCameras);
      } else {
        setErrorCameras(response.message || 'Không thể tải danh sách camera');
        setCameras([]);
      }
    } catch (err: any) {
      console.error('Error fetching cameras:', err);
      setErrorCameras(err.message || 'Có lỗi xảy ra khi tải danh sách camera');
      setCameras([]);
    } finally {
      setLoadingCameras(false);
    }
  }, []);
  
  // Thêm nhật ký hàng loạt
  const addNhatKyByCaySamIds = useCallback(async (
    data: AddNhatKyBatchData, 
    files: File[] = [] 
  ): Promise<boolean> => {
    try {
      setLoadingAddNhatKy(true);
      setErrorAddNhatKy(null);
      
      const response = await caySamApiService.AddNhatKyByCaySamIds(data, files);
      
      if (response.messCode === 1) {
        return true;
      }
      setErrorAddNhatKy(response.message || 'Không thể thêm nhật ký cây sâm');
      return false;
    } catch (err: any) {
      console.error('Error adding nhat ky by cay sam ids:', err);
      setErrorAddNhatKy(err.message || 'Có lỗi xảy ra khi thêm nhật ký cây sâm');
      return false;
    } finally {
      setLoadingAddNhatKy(false);
    }
  }, []);

  // Thêm file đính kèm
  const addDinhKemFile = useCallback(async (
    caySamIds: string[], 
    file: File
  ): Promise<boolean> => {
    try {
      setLoadingAddFile(true);
      setErrorAddFile(null);
      
      const fileData = {
        CaySam_IDs: caySamIds
      };

      const response = await caySamApiService.AddDinhKemFileCaySam(fileData, file);
      
      if (response.messCode === 1) {
        return true;
      }
      
      setErrorAddFile(response.message || 'Không thể thêm tệp đính kèm');
      return false;

    } catch (err: any) {
      console.error('Error adding dinh kem file:', err);
      setErrorAddFile(err.message || 'Có lỗi xảy ra khi thêm tệp đính kèm');
      return false;
    } finally {
      setLoadingAddFile(false);
    }
  }, []);

  const clearData = useCallback(() => {
    setCaySam(null);
    setCaySamNhatKy([]);
    setCameras([]); // <--- Reset cameras
    
    setError(null);
    setErrorNhatKy(null);
    setErrorAddNhatKy(null);
    setErrorAddFile(null); 
    setErrorCameras(null); // <--- Reset error cameras
    
    setLoading(false);
    setLoadingNhatKy(false);
    setLoadingAddNhatKy(false);
    setLoadingAddFile(false); 
    setLoadingCameras(false); // <--- Reset loading cameras
  }, []);

  return {
    caySam,
    caySamNhatKy,
    cameras, // <--- Export
    loading,
    loadingNhatKy,
    loadingAddNhatKy,
    loadingAddFile,
    loadingCameras, // <--- Export
    error,
    errorNhatKy,
    errorAddNhatKy,
    errorAddFile,
    errorCameras, // <--- Export
    getCaySam,
    getCaySamNhatKy,
    getCamerasByLoSamId, // <--- Export
    addNhatKyByCaySamIds,
    addDinhKemFile,
    clearData,
  };
}

// ----------------------------------------------------------------------
// Phần Hook Camera (Giữ nguyên logic cũ)

interface CameraState {
  angle: number;
  range: number;
  spread: number;
}

type UseCameraReturn = {
  streamUri: string | null;
  streamContent: string | null;
  loading: boolean;
  error: string | null;
  startCamera: (camera: LoSamCamera) => Promise<void>;
  stopCamera: () => void;
  cameraState: CameraState | null;
};

export function useCamera(): UseCameraReturn {
  const [streamUri, setStreamUri] = useState<string | null>(null);
  const [streamContent, setStreamContent] = useState<string | null>(null);
  const [cameraState, setcameraState] = useState<CameraState | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startCamera = useCallback(async (camera: LoSamCamera) => {
    if (!camera) {
      setError('Thông tin camera không hợp lệ');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const cameraData = {
        id: camera.id, 
        loSamId: camera.loSamId,
        loSamLoaiCameraId: camera.loSamLoaiCameraId,
        ipCamera: camera.ipCamera,
        onvifCamera: camera.onvifCamera,
        rtsp: camera.rtsp,
        userName: camera.userName || '',
        password: camera.password || '',
        trangThai: camera.trangThai,
        action : camera.action || ''
      };

      const response = await caySamApiService.startStreamCamera(cameraData);
      
      if (response != null) {
        const streamUrl = response.uri || '' ;
        setStreamUri(streamUrl);  
        setcameraState(response.cameraState);
        setStreamContent(response.message || 'Camera stream started');
      } else {
        setError(response.message || 'Không thể khởi động camera');
        setStreamUri(null);
        setStreamContent(null);
      }
    } catch (err: any) {
      console.error('Error starting camera:', err);
      setError(err.message || 'Có lỗi xảy ra khi khởi động camera');
      setStreamUri(null);
      setStreamContent(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const stopCamera = useCallback(() => {
    setStreamUri(null);
    setStreamContent(null);
    setError(null);
  }, []);

  return {
    streamUri,
    streamContent,
    cameraState,
    loading,
    error,
    startCamera,
    stopCamera,
  };
}